/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React from 'react';
import styled, { css } from 'styled-components';
import { translate } from '../../common/translation/translate';
import { defaultTextValue } from '../../common/utilities/constants';

import { HurricaneCategoryNumber, SpeedUnit, Basin, HurricaneCategory, DistanceUnit } from '../common/interfaces/types';
import { withScreenSize } from '../common/utilities/withScreenSize.jsx';
import { HurricaneWidgetType } from './types';

interface HurricaneWidgetCaptionTextProps {
    hurricaneIconType?: typeof HurricaneWidgetType[keyof typeof HurricaneWidgetType];
    hurricaneOutlookWidgetCaptionText?: string;
    activeHurricaneName?: string;
    hurricaneCategory?: HurricaneCategory;
    hurricaneWindSpeed?: number;
    hurricaneWindSpeedUnit?: SpeedUnit | '';
    hurricaneDistance?: number;
    hurricaneDistanceUnit?: DistanceUnit | '';
    regionEffectedByHurricane?: typeof Basin[keyof typeof Basin] | '';
    isTablet: boolean;
}

export const HurricaneWidgetCaptionText = withScreenSize(({ 
    isTablet,
    hurricaneIconType=HurricaneWidgetType.noActiveHurricaneOrWarning,
    activeHurricaneName=defaultTextValue,
    hurricaneCategory,
    hurricaneWindSpeed,
    hurricaneWindSpeedUnit='',
    hurricaneDistance,
    hurricaneDistanceUnit='',
    regionEffectedByHurricane='', 
    hurricaneOutlookWidgetCaptionText }: HurricaneWidgetCaptionTextProps) => {

    if (hurricaneIconType === HurricaneWidgetType.activeHurricane) {
        const categoryDisplay = hurricaneCategory ? hurricaneCategory : '';
        const hurricaneSpeedDisplay = Number.isFinite(hurricaneWindSpeed) ? `${hurricaneWindSpeed} ${hurricaneWindSpeedUnit}` : '';
        const hurricaneDistanceDisplay = Number.isFinite(hurricaneDistance) ? `${hurricaneDistance} ${hurricaneDistanceUnit} ${translate('away')}` : '';

        if (isTablet) {
            return (
                <ActiveHurricaneCaptionTextContainer>
                    <ActiveHurricaneDetailsTextTablet>
                        {`${activeHurricaneName} • ${categoryDisplay} • ${hurricaneSpeedDisplay} • ${hurricaneDistanceDisplay}`}
                    </ActiveHurricaneDetailsTextTablet>
                </ActiveHurricaneCaptionTextContainer>
            );
        }

        return (
            <ActiveHurricaneCaptionTextContainer>
                <ActiveHurricaneDetailsText>
                    {`${activeHurricaneName} • ${categoryDisplay}`}
                </ActiveHurricaneDetailsText>
                <ActiveHurricaneDetailsText>
                    {`${hurricaneSpeedDisplay} • ${hurricaneDistanceDisplay}`}
                </ActiveHurricaneDetailsText>
            </ActiveHurricaneCaptionTextContainer>
        );
    }

    // default return Hurricane Outlook Caption UI
    // this will be same UI used when there's Hurricane outlook or when there's no (active hurricane + hurricane outlook) data
    return (
        <HurricaneOutlookCaptionText isHurricaneOutlookSummaryData={hurricaneIconType === HurricaneWidgetType.hurricaneOutlook}>
            {hurricaneOutlookWidgetCaptionText}
        </HurricaneOutlookCaptionText>
    );
});

const CommonWidgetCaptionTextStyle = css`
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: #555555;
`;

interface HurricaneOutlookCaptionTextProps {
    isHurricaneOutlookSummaryData?: boolean;
}
const HurricaneOutlookCaptionText = styled.div<HurricaneOutlookCaptionTextProps>`
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${({ isHurricaneOutlookSummaryData }) => isHurricaneOutlookSummaryData ? 2 : 3};
    overflow: hidden;
    align-items: center;
    ${CommonWidgetCaptionTextStyle}
`;

const ActiveHurricaneCaptionTextContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex-basis: 100%;
    height: 100%;
    justify-content: space-between;
`;

const ActiveHurricaneDetailsText = styled.span`
    display: flex;
    flex-basis: 100%;
    height: 49%;
    ${CommonWidgetCaptionTextStyle}
`;

const ActiveHurricaneDetailsTextTablet = styled.span`
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    ${CommonWidgetCaptionTextStyle}
`;